import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getDeliveryCertificates, DeliveryCertificate, DeliveryCertificatesResponse } from '../../../services/ApiService';
import { useSystemTables } from '../../../hooks/UseSystemTables';
import { format } from 'date-fns';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import Spinner from '../../../components/Spinner/Spinner';

const DeliveryCertificates: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    clientName: '',
    productId: '',
    onlyWithImages: false,
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTimestamp, setSearchTimestamp] = useState(Date.now());
  const [selectedCertificate, setSelectedCertificate] = useState<DeliveryCertificate | null>(null);
  const [enlargedImage, setEnlargedImage] = useState<{ url: string; alt: string } | null>(null);

  const { data: systemTables } = useSystemTables();

  const { data, isLoading, error, refetch } = useQuery<DeliveryCertificatesResponse>({
    queryKey: ['deliveryCertificates', appliedFilters, page, searchTimestamp],
    queryFn: () => getDeliveryCertificates({ ...appliedFilters, page }),
  });

  // Set direction to rtl if the language is Hebrew
  const contentDirection = i18n.language === 'he' ? 'rtl' : 'ltr';

  const handleSearch = () => {
    setPage(1); // Reset to first page on new search
    setAppliedFilters(filters);
    setSearchTimestamp(Date.now());
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClientNameChange = (
    event: React.SyntheticEvent,
    newValue: { value: string; label: string } | null
  ) => {
    setFilters(prev => ({ ...prev, clientName: newValue?.value || '' }));
  };

  const handleOnlyWithImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, onlyWithImages: e.target.checked }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRowClick = (certificate: DeliveryCertificate) => {
    setSelectedCertificate(certificate);
  };

  const handleCloseDialog = () => {
    setSelectedCertificate(null);
  };

  const handleImageClick = (url: string, alt: string) => {
    setEnlargedImage({ url, alt });
  };

  const handleCloseEnlargedImage = () => {
    setEnlargedImage(null);
  };

  const handleDownloadImage = (url: string, alt: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the image click handler
    
    // Extract filename from URL or use alt text
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1].split('?')[0] || `${alt.replace(/\s+/g, '_')}.jpg`;
    
    // Find the existing image element in the DOM (sibling of the button)
    const buttonElement = event.currentTarget as HTMLElement;
    const imageContainer = buttonElement.parentElement;
    const imgElement = imageContainer?.querySelector('img') as HTMLImageElement;
    
    if (imgElement && imgElement.complete && imgElement.naturalWidth > 0) {
      // Image is already loaded, use it directly
      try {
        // Create a canvas and draw the existing image
        const canvas = document.createElement('canvas');
        canvas.width = imgElement.naturalWidth;
        canvas.height = imgElement.naturalHeight;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }
        
        ctx.drawImage(imgElement, 0, 0);
        
        // Convert canvas to blob and download
        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(link);
          } else {
            throw new Error('Failed to create blob from canvas');
          }
        }, 'image/jpeg', 0.95);
      } catch (error) {
        console.error('Error processing image from DOM:', error);
        // Fallback: try direct download link
        tryDirectDownload(url, fileName);
      }
    } else {
      // Image not loaded yet, try direct download
      tryDirectDownload(url, fileName);
    }
  };

  const tryDirectDownload = (url: string, fileName: string) => {
    // Try direct download link (works for presigned URLs if CORS allows)
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom 
        sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          mb: 3 
        }}
      >
        {t('DeliveryCertificates.Title')}
      </Typography>

      {/* Filters Section */}
      <Paper sx={{ mb: 3, backgroundColor: '#fafafa' }}>
        <Box mb={2}>
          <Button
            variant="contained"
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          >
            {isFiltersExpanded ? t('Filters.Close') : t('Filters.Open')}
          </Button>
        </Box>

        <Collapse in={isFiltersExpanded}>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label={t('General.Labels.StartDate')}
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                onKeyPress={handleKeyPress}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label={t('General.Labels.EndDate')}
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                onKeyPress={handleKeyPress}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <Autocomplete
                fullWidth
                options={systemTables?.data.clients.map(client => ({
                  value: client.clientName,
                  label: client.clientName,
                })) || []}
                getOptionLabel={(option) => option.label}
                value={
                  systemTables?.data.clients
                    .map(client => ({ value: client.clientName, label: client.clientName }))
                    .find(opt => opt.value === filters.clientName) || null
                }
                onChange={handleClientNameChange}
                filterOptions={(options, { inputValue }) => {
                  // If no input, show top 5
                  if (!inputValue) {
                    return options.slice(0, 5);
                  }
                  // If input exists, filter by label (case-insensitive)
                  const filtered = options.filter((option) =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('DeliveryCertificates.Labels.ClientName')}
                    onKeyPress={handleKeyPress}
                  />
                )}
              />

              <TextField
                select
                label={t('DeliveryCertificates.Labels.Product')}
                name="productId"
                value={filters.productId}
                onChange={handleFilterChange}
                onKeyPress={handleKeyPress}
                fullWidth
              >
                <MenuItem value="">Select Product</MenuItem>
                {systemTables?.data.catalog.map(item => (
                  <MenuItem key={item.itemNumber} value={item.itemNumber}>
                    {item.itemNumber}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.onlyWithImages}
                    onChange={handleOnlyWithImagesChange}
                    name="onlyWithImages"
                  />
                }
                label={t('DeliveryCertificates.Labels.OnlyWithImages')}
              />
            </Box>

            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? 'Searching...' : t('Filters.Apply')}
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Results Section */}
      <Paper>
        <Box sx={{ p: 2 }}>
          {isLoading && <Spinner />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading delivery certificates
            </Alert>
          )}

          {data && (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('DeliveryCertificates.Labels.CertificateNumber')}</TableCell>
                      <TableCell>{t('DeliveryCertificates.Labels.DeliveryDate')}</TableCell>
                      <TableCell>{t('DeliveryCertificates.Labels.ClientName')}</TableCell>
                      <TableCell>{t('DeliveryCertificates.Labels.Origin')}</TableCell>
                      <TableCell>{t('DeliveryCertificates.Labels.CartNote')}</TableCell>
                      <TableCell>{t('DeliveryCertificates.Labels.Product')}</TableCell>
                      <TableCell>{t('DeliveryCertificates.Labels.Driver')}</TableCell>
                      <TableCell>{t('DeliveryCertificates.Labels.TruckNumber')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.shipments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No results found
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.shipments.map((certificate) => (
                        <TableRow 
                          key={certificate._id} 
                          hover 
                          onClick={() => handleRowClick(certificate)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{certificate.shippingCertificateNumber}</TableCell>
                          <TableCell>{format(new Date(certificate.date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell>{certificate.clientName}</TableCell>
                          <TableCell>{certificate.siteOfOrigin}</TableCell>
                          <TableCell>{certificate.cartNote}</TableCell>
                          <TableCell>{certificate.itemDescription}</TableCell>
                          <TableCell>{certificate.driverName}</TableCell>
                          <TableCell>{certificate.truckNum}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {data.shipments.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('Pagination.Labels.RowsPerPage')}:
                    <select
                      value={10}
                      onChange={(/*e*/) => {
                        // TODO: Implement rows per page change
                      }}
                    >
                      {[10, 20, 30, 40, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          {pageSize}
                        </option>
                      ))}
                    </select>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setPage(page - 1);
                        refetch();
                      }}
                      disabled={page <= 1}
                      sx={{ minWidth: '100px' }}
                    >
                      {t('Pagination.Labels.PreviousPage')}
                    </Button>
                    <Box sx={{ minWidth: '150px', textAlign: 'center' }}>
                      {t('Pagination.Labels.CurrentPage', { 
                        currentPage: page, 
                        totalPages: data.totalPages 
                      })}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setPage(page + 1);
                        refetch();
                      }}
                      disabled={page >= data.totalPages}
                      sx={{ minWidth: '100px' }}
                    >
                      {t('Pagination.Labels.NextPage')}
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Results: {data.total}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Certificate Details Dialog */}
      <Dialog 
        open={!!selectedCertificate} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        dir={contentDirection}
      >
        {selectedCertificate && (
          <>
            <DialogTitle sx={{ 
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 2
            }}>
              <Typography variant="h6" component="div">
                {t('DeliveryCertificates.Dialog.Title', { 
                  number: selectedCertificate.shippingCertificateNumber 
                })}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {[
                  { label: 'DeliveryCertificates.Labels.DeliveryDate', value: format(new Date(selectedCertificate.date), 'dd/MM/yyyy') },
                  { label: 'DeliveryCertificates.Labels.ClientName', value: selectedCertificate.clientName },
                  { label: 'DeliveryCertificates.Labels.Origin', value: selectedCertificate.siteOfOrigin },
                  { label: 'DeliveryCertificates.Labels.Destination', value: selectedCertificate.destination },
                  { label: 'DeliveryCertificates.Labels.LoadingTime', value: selectedCertificate.loadingTime },
                  { label: 'DeliveryCertificates.Labels.TransportationCompany', value: selectedCertificate.transportationCompany },
                  { label: 'DeliveryCertificates.Labels.Driver', value: selectedCertificate.driverName },
                  { label: 'DeliveryCertificates.Labels.TruckNumber', value: selectedCertificate.truckNum },
                  { label: 'DeliveryCertificates.Labels.Product', value: selectedCertificate.itemDescription },
                  { label: 'DeliveryCertificates.Labels.Manufacturer', value: selectedCertificate.manufacturer },
                  { label: 'DeliveryCertificates.Labels.DestinationReceiver', value: selectedCertificate.destinationReceiverName },
                  { label: 'DeliveryCertificates.Labels.Comments', value: selectedCertificate.comments },
                  { label: 'DeliveryCertificates.Labels.CartNote', value: selectedCertificate.cartNote },
                ].map((item/*, index*/) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2,
                        backgroundColor: 'action.hover',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 'bold' }}
                      >
                        {t(item.label)}:
                      </Typography>
                      <Typography variant="body1">
                        {item.value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Images Section */}
              {selectedCertificate.driverUploadedFiles && selectedCertificate.driverUploadedFiles.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ mb: 2, fontWeight: 'bold' }}
                    >
                      {t('DeliveryCertificates.Labels.DeliveryImages')}
                    </Typography>
                  </Grid>
                  {selectedCertificate.driverUploadedFiles.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 1,
                          backgroundColor: 'action.hover',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        <Box 
                          display="flex" 
                          flexDirection="column" 
                          alignItems="center"
                          sx={{ 
                            cursor: 'pointer',
                            position: 'relative',
                            '&:hover .download-button': {
                              opacity: 1,
                            },
                          }}
                          onClick={() => handleImageClick(file.presignedUrl, `Delivery Image ${index + 1}`)}
                        >
                          <img 
                            src={file.presignedUrl} 
                            alt={`Delivery Image ${index + 1}`}
                            crossOrigin="anonymous"
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '200px', 
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                          <IconButton
                            className="download-button"
                            onClick={(e) => handleDownloadImage(file.presignedUrl, `Delivery Image ${index + 1}`, e)}
                            sx={{
                              position: 'absolute',
                              top: -2,
                              left: -1,
                              width: 'fit-content',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              opacity: 0,
                              transition: 'opacity 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                            }}
                            aria-label="Download image"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ 
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 2
            }}>
              <Button 
                onClick={handleCloseDialog}
                variant="contained"
              >
                {t('General.Labels.Close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Enlarged Image Dialog */}
      <Dialog
        open={!!enlargedImage}
        onClose={handleCloseEnlargedImage}
        maxWidth="lg"
        fullWidth
        dir={contentDirection}
      >
        <DialogTitle>{enlargedImage?.alt}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center">
            <img 
              src={enlargedImage?.url} 
              alt={enlargedImage?.alt}
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} 
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEnlargedImage} variant="contained">
            {t('General.Labels.Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryCertificates; 